import type { MapPin, Person } from "../models/types";

interface LocationOverride {
  city: string;
  state: string;
}

interface EffectiveLocation {
  city: string | null;
  state: string | null;
  lat: number;
  lng: number;
}

function effectiveMapLocation(
  person: Person,
  overrides: Map<string, LocationOverride>,
): EffectiveLocation | null {
  const o = overrides.get(person.person_id);
  const lat = person.location_lat;
  const lng = person.location_lng;
  if (lat == null || lng == null) return null;
  return {
    city: o?.city ?? person.location_city ?? null,
    state: o?.state ?? person.location_state ?? null,
    lat,
    lng,
  };
}

function coordKey(lat: number, lng: number): string {
  const latKey = Math.round(lat * 1000) / 1000;
  const lngKey = Math.round(lng * 1000) / 1000;
  return `${latKey.toFixed(3)},${lngKey.toFixed(3)}`;
}

/**
 * Mirrors iOS `SundiusFamilyNewsStore.buildMapPins`:
 * regroup living people by rounded lat/lng, reuse bundled label/photo when person set
 * (or coordinate) matches a bundled pin — so labels like "Brennan family" survive.
 */
export function buildMapPins(
  people: Person[],
  bundled: MapPin[],
  overrides: Map<string, LocationOverride> = new Map(),
): MapPin[] {
  const bundledByPeople = new Map<string, MapPin>();
  const bundledByCoord = new Map<string, MapPin>();
  for (const pin of bundled) {
    bundledByPeople.set([...pin.person_ids].sort().join("|"), pin);
    bundledByCoord.set(coordKey(pin.location_lat, pin.location_lng), pin);
  }

  type Mapped = {
    person: Person;
    city: string | null;
    state: string | null;
    lat: number;
    lng: number;
  };

  const mapped: Mapped[] = [];
  for (const person of people) {
    if (person.is_deceased) continue;
    const location = effectiveMapLocation(person, overrides);
    if (!location) continue;
    mapped.push({ person, ...location });
  }

  const groups = new Map<string, Mapped[]>();
  for (const member of mapped) {
    const key = coordKey(member.lat, member.lng);
    const list = groups.get(key) ?? [];
    list.push(member);
    groups.set(key, list);
  }

  const pins: MapPin[] = [];
  for (const [key, members] of groups) {
    const sorted = [...members].sort((a, b) =>
      a.person.display_name.localeCompare(b.person.display_name, undefined, {
        sensitivity: "base",
      }),
    );
    const personIds = sorted.map((m) => m.person.person_id);
    const metaKey = [...personIds].sort().join("|");
    const meta = bundledByPeople.get(metaKey) ?? bundledByCoord.get(key);
    const anchor = sorted[0]!;
    const label =
      meta?.label ??
      (sorted.length === 1
        ? anchor.person.display_name
        : sorted.map((m) => m.person.display_name).join(", "));
    pins.push({
      id: meta?.id ?? personIds.join("-"),
      person_ids: personIds,
      label,
      photo: meta?.photo ?? (sorted.length === 1 ? anchor.person.photo : null),
      location_city: anchor.city,
      location_state: anchor.state,
      location_lat: anchor.lat,
      location_lng: anchor.lng,
    });
  }

  return pins.sort((a, b) => a.label.localeCompare(b.label));
}
